/* The trees the field app draws (`27` section 8): the ones the arborist holds,
 * read from their open list (GET /api/field-audits/assignments, 15 section
 * 3.15), and the ones they recorded as missed on this phone. A held tree is
 * named by its project and its span's poles, and shows Kanopy's band; a held
 * missed tree, one somebody recorded and synced and was then sent, is named
 * by its placement and has no band.
 *
 * Not a copy of the open list kept on the phone: the list is read over the
 * network, which revision one has in the field (`27` section 6), and only the
 * arborist's own work is kept on the phone (unsyncedWork.ts).
 */
import { useQueries } from "@tanstack/react-query";
import { useOpenAssignments } from "../api/fieldAudits";
import { useMirrorProjects } from "../api/projects";
import { spanQuery } from "../api/spans";
import type { AssignmentRecord, Project, SpanRecord, TreeRecord } from "../api/types";
import { kanopyHandle, metersToFeet, shortId, spanName } from "../lib/format";
import { photoUrl, type AuditValues, type MissedTree, type UnsyncedAudit } from "./unsyncedWork";

/* Kanopy's band and the reading behind it, as TreeBandBadge draws a tree's. */
export type TreeBand = Pick<TreeRecord, "condition_band" | "assessment">;

/* A missed tree's, held or recorded here: FieldTree's `band` says why. */
const NO_BAND: TreeBand = { condition_band: null, assessment: "not_assessed" };

/* A tree as the screens draw it. Distances are in feet, as the screens show
 * and set them, and null where nothing was measured. */
export interface FieldTree {
  /* Our tree's or held missed tree's id, or the label of a missed tree
   * recorded here: the key its work on the phone is kept under
   * (unsyncedWork.ts's `treeKey`). */
  id: string;
  label: string;
  /* The assignment a held tree came with, which its audit names (`20`
   * section 3.12b). A missed tree recorded here has none. */
  assignmentId: string | null;
  lat: number;
  lng: number;
  project: string;
  /* A held tree's span by its poles, or the span a held missed tree is
   * placed on, or the pole a missed tree was recorded at here; null where
   * none is known. */
  span: string | null;
  species: string | null;
  /* A missed tree's recorded here, as the arborist entered it: the open list
   * carries no tree height. */
  height: number | null;
  clearance: number | null;
  encroach: number | null;
  /* A missed tree, which Kanopy never saw, has no band and reads not
   * assessed: its step is scored on the server (AR-369), and is not a band. */
  band: TreeBand;
  pics: string[];
  source?: "field";
  repinned?: boolean;
}

/* Feet to a tenth, the precision the screens show and step by, so a value the
 * arborist leaves alone compares equal to the one Kanopy sent. */
function feet(m: number | null): number | null {
  return m === null ? null : Math.round(metersToFeet(m) * 10) / 10;
}

/* A held tree of either kind, named by `projectName` and `span` where they
 * have been read. */
export function heldTree(
  assignment: AssignmentRecord,
  projectName: string | undefined,
  span: SpanRecord | undefined,
): FieldTree {
  const held = {
    assignmentId: assignment.id,
    project: projectName ?? "—",
    span: span ? spanName(span.from_kanopy_pole_id, span.to_kanopy_pole_id) : null,
    height: null,
    pics: [],
  };
  if (assignment.tree) {
    const t = assignment.tree;
    return {
      ...held,
      id: t.id,
      label: kanopyHandle(t.kanopy_tree_id),
      lat: t.location.lat,
      lng: t.location.lon,
      species: t.species_primary,
      clearance: feet(t.min_absolute_distance_m),
      encroach: feet(t.encroachment_height_m),
      band: { condition_band: t.condition_band, assessment: t.assessment },
    };
  }
  // Kanopy has no id for it, so it goes by ours.
  const m = assignment.missed_tree;
  return {
    ...held,
    id: m.id,
    label: shortId(m.id),
    lat: m.location.lat,
    lng: m.location.lon,
    species: m.species_primary,
    clearance: feet(m.min_absolute_distance_m),
    encroach: feet(m.encroachment_height_m),
    band: NO_BAND,
  };
}

/* The project a held tree is named by: a tree's, from the project names read,
 * or a missed tree's placement, which carries its name; an unplaced one says
 * so (`27` section 8). */
function heldProject(a: AssignmentRecord, names: ReadonlyMap<string, string>): string | undefined {
  if (a.tree) return names.get(a.tree.project_id);
  return a.missed_tree.placement?.project_name ?? "Unplaced";
}

/* The span a held tree is on: a tree's own, or the one a missed tree is
 * placed on. */
function heldSpanId(a: AssignmentRecord): string | null {
  return a.tree ? a.tree.span_id : (a.missed_tree.placement?.span_id ?? null);
}

/* A tree the arborist recorded as missed, from its record on this phone. */
export function missedTree({ photos, site, pole, ...tree }: MissedTree): FieldTree {
  return {
    ...tree,
    label: tree.id,
    assignmentId: null,
    project: site,
    span: pole || null,
    band: NO_BAND,
    pics: photos.map(photoUrl),
    source: "field",
  };
}

/* What a correction records of the values the form shows: each the arborist
 * set to something other than the tree's own. One left alone is never sent
 * back as a change, and one cleared to nothing records nothing, since an
 * empty field is not a measurement. */
export function changedValues(
  tree: FieldTree,
  entered: { species: string; clearance: number | null; encroach: number | null },
): Pick<AuditValues, "species" | "clearance" | "encroach"> {
  return {
    ...(entered.species !== "" && entered.species !== tree.species ? { species: entered.species } : {}),
    ...(entered.clearance !== null && entered.clearance !== tree.clearance ? { clearance: entered.clearance } : {}),
    ...(entered.encroach !== null && entered.encroach !== tree.encroach ? { encroach: entered.encroach } : {}),
  };
}

/* Release is offered only on a held tree with no work on this phone: the
 * sync refuses an audit whose assignment is released (15 section 3.15a), so
 * releasing first would strand the work. */
export function mayRelease(tree: FieldTree, records: readonly UnsyncedAudit[]): boolean {
  return tree.assignmentId !== null && !records.some((r) => r.treeKey === tree.id);
}

/* The held trees, undefined until the open list and the project names have
 * both arrived. A span's name follows as its read lands, and a span that
 * cannot be read leaves its tree without one rather than holding the list
 * back. `spans` holds whatever span reads have landed, in any order. */
export function heldTrees(
  assignments: readonly AssignmentRecord[] | undefined,
  projects: readonly Project[] | undefined,
  spans: readonly (SpanRecord | undefined)[],
): FieldTree[] | undefined {
  if (!assignments || !projects) return undefined;
  const names = new Map(projects.map((p) => [p.id, p.name]));
  const read = new Map(spans.flatMap((s) => (s ? [[s.id, s] as const] : [])));
  return assignments.map((a) => {
    const spanId = heldSpanId(a);
    return heldTree(a, heldProject(a, names), spanId ? read.get(spanId) : undefined);
  });
}

/* The trees the caller holds, read as `heldTrees` says. */
export function useHeldTrees(): { trees: FieldTree[] | undefined; error: Error | null } {
  const open = useOpenAssignments();
  const projects = useMirrorProjects();
  const spanIds = [...new Set((open.items ?? []).flatMap((a) => heldSpanId(a) ?? []))];
  const spans = useQueries({ queries: spanIds.map(spanQuery) });
  return {
    trees: heldTrees(open.items, projects.items, spans.map((q) => q.data)),
    error: open.error ?? projects.error,
  };
}
